import React, {useEffect, useRef} from 'react';
import {Flex} from '@chakra-ui/react';

// ═══════════════════════════════════════════════════════════════
// AUDIO WAVEFORM (Canvas)
// ═══════════════════════════════════════════════════════════════
//
// Canvas-based live waveform. Pulls per-frame frequency data from a
// session-manager-like object that exposes `getAudioFrequencyData(binCount)`
// returning an array of normalized (0..1) values. Used by the live scribe
// view during visits and by the post-visit dictation panel.

const DEFAULT_WAVEFORM_BARS = 40;
const WAVEFORM_INTERVAL = 1000 / 30;

function drawRoundedBar(ctx: any, x: any, y: any, w: any, h: any, r: any) {
  if (h < 0.5 || w < 0.5) return;
  const rad = Math.min(r, h / 2, w / 2);
  ctx.moveTo(x + rad, y);
  ctx.lineTo(x + w - rad, y);
  ctx.arcTo(x + w, y, x + w, y + rad, rad);
  ctx.lineTo(x + w, y + h - rad);
  ctx.arcTo(x + w, y + h, x + w - rad, y + h, rad);
  ctx.lineTo(x + rad, y + h);
  ctx.arcTo(x, y + h, x, y + h - rad, rad);
  ctx.lineTo(x, y + rad);
  ctx.arcTo(x, y, x + rad, y, rad);
  ctx.closePath();
}

const DEFAULT_GRADIENT_STOPS = [
  [0, 'rgba(156, 156, 156,0.55)'],
  [0.3, 'rgba(115, 115, 115,0.88)'],
  [0.5, 'rgba(99, 99, 99,1)'],
  [0.7, 'rgba(115, 115, 115,0.88)'],
  [1, 'rgba(156, 156, 156,0.55)'],
];

const DEFAULT_GLOW_COLOR = 'rgba(99, 99, 99,';

const AudioWaveform = React.memo(
  ({
    // Anything with `.current.getAudioFrequencyData(bars)` returning an
    // array of normalized 0..1 values. Can be a React ref whose current is
    // the session manager, or a plain object.
    sessionManagerRef,
    isRecording,
    isPaused,
    bars = DEFAULT_WAVEFORM_BARS,
    width = '100%',
    maxWidth = '340px',
    height = '80px',
    // Custom visual theme (defaults to Scribe blue). Pass an array of
    // [offset, color] pairs for the active-bar gradient.
    gradientStops = DEFAULT_GRADIENT_STOPS,
    glowColorPrefix = DEFAULT_GLOW_COLOR,
    mb = 6,
  }: Record<string, any>) => {
    const canvasRef = useRef<any>(null);
    const smoothedRef = useRef(new Float32Array(bars));
    const rafRef = useRef<any>(null);
    const sizeRef = useRef<{w: number; h: number}>({w: 0, h: 0});
    const barGradRef = useRef<any>(null);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return undefined;
      const ctx = canvas.getContext('2d');
      let active = true;
      let lastFrame = 0;

      // Resize buffer if `bars` prop changed between mounts.
      if (smoothedRef.current.length !== bars) {
        smoothedRef.current = new Float32Array(bars);
      }

      const onResize = () => {
        const r = canvas.getBoundingClientRect();
        sizeRef.current = {w: r.width, h: r.height};
        barGradRef.current = null;
      };
      onResize();
      const ro = new ResizeObserver(onResize);
      ro.observe(canvas);

      const draw = (ts: any) => {
        if (!active) return;

        if (ts - lastFrame < WAVEFORM_INTERVAL) {
          rafRef.current = requestAnimationFrame(draw);
          return;
        }
        lastFrame = ts;

        const {w, h} = sizeRef.current;
        if (w === 0 || h === 0) {
          rafRef.current = requestAnimationFrame(draw);
          return;
        }

        const dpr = window.devicePixelRatio || 1;
        const pw = Math.round(w * dpr);
        const ph = Math.round(h * dpr);
        if (canvas.width !== pw || canvas.height !== ph) {
          canvas.width = pw;
          canvas.height = ph;
          barGradRef.current = null;
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        let raw;
        if (sessionManagerRef?.current?.getAudioFrequencyData) {
          raw = sessionManagerRef.current.getAudioFrequencyData(bars);
        }
        if (!raw) raw = Array.from({length: bars}, () => 0);

        const sm = smoothedRef.current;
        const lerpAlpha = isRecording ? 0.28 : 0.1;
        for (let i = 0; i < bars; i += 1) {
          sm[i] += ((raw[i] || 0) - sm[i]) * lerpAlpha;
        }

        const gap = 2.5;
        const barW = (w - gap * (bars - 1)) / bars;
        const midY = h * 0.46;
        const maxH = midY - 3;
        const now = ts / 1000;

        if (isRecording && !barGradRef.current) {
          const g = ctx.createLinearGradient(0, midY - maxH, 0, midY + maxH);
          gradientStops.forEach(([offset, color]: any) =>
            g.addColorStop(offset, color),
          );
          barGradRef.current = g;
        }

        if (isRecording) {
          let avg = 0;
          for (let i = 0; i < bars; i += 1) avg += sm[i];
          avg /= bars;
          if (avg > 0.025) {
            const glow = ctx.createRadialGradient(
              w / 2,
              midY,
              0,
              w / 2,
              midY,
              w * 0.5,
            );
            glow.addColorStop(
              0,
              `${glowColorPrefix}${Math.min(0.1, avg * 0.25)})`,
            );
            glow.addColorStop(1, `${glowColorPrefix}0)`);
            ctx.fillStyle = glow;
            ctx.fillRect(0, 0, w, h);
          }
        }

        if (isRecording) {
          ctx.fillStyle = barGradRef.current;
        } else if (isPaused) {
          ctx.fillStyle = 'rgba(163, 163, 163,0.45)';
        } else {
          ctx.fillStyle = 'rgba(181, 181, 181,0.18)';
        }

        for (let i = 0; i < bars; i += 1) {
          const v = sm[i];
          const breathe = isRecording
            ? Math.sin(now * 1.6 + i * 0.22) * 0.012 + 0.012
            : 0;
          const barH = Math.max(1.5, (v + breathe) * maxH);
          const x = i * (barW + gap);
          const r = Math.min(barW * 0.5, 2.5);

          const intensity = isRecording
            ? 0.38 + 0.62 * Math.min(1, v * 2.2)
            : isPaused
              ? 0.25 + 0.55 * Math.min(1, v * 3)
              : 0.5;

          ctx.globalAlpha = intensity;
          ctx.beginPath();
          drawRoundedBar(ctx, x, midY - barH, barW, barH, r);
          ctx.fill();

          ctx.globalAlpha = intensity * 0.3;
          ctx.beginPath();
          drawRoundedBar(ctx, x, midY + 1, barW, barH * 0.55, r);
          ctx.fill();
        }
        ctx.globalAlpha = 1;

        if (isRecording || isPaused) {
          rafRef.current = requestAnimationFrame(draw);
        }
      };

      rafRef.current = requestAnimationFrame(draw);
      return () => {
        active = false;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        ro.disconnect();
      };
    }, [
      isRecording,
      isPaused,
      sessionManagerRef,
      bars,
      gradientStops,
      glowColorPrefix,
    ]);

    return (
      <Flex justify="center" mb={mb}>
        <canvas
          ref={canvasRef}
          style={{width, maxWidth, height, display: 'block'}}
          aria-hidden="true"
        />
      </Flex>
    );
  },
);

AudioWaveform.displayName = 'AudioWaveform';

export default AudioWaveform;
